import { IFixitAPIClient } from '@ifixit/ifixit-api-client';
import { captureMessage } from '@ifixit/sentry';
import { z } from 'zod';

export type User = z.infer<typeof AuthenticatedUserSchema>;

export const userKeys = {
   user: ['user'],
};

export const userDataLocalKey = 'user.data.5';

export const AuthenticatedUserSchema = z.object({
   id: z.number(),
   username: z.string(),
   handle: z.string().nullable(),
   login: z.string(),
   thumbnail: z.string().nullable(),
   algoliaApiKeyProducts: z.string().nullable(),
   priceTiers: z.record(z.string()),
   isAdmin: z.boolean(),
   teams: z.array(z.number()),
   links: z.record(z.any()),
   unread_notification_count: z.number().optional().nullable(),
   privileges: z.array(z.string()),
   greatest_privilege: z.string().optional(),
   isEmployee: z.boolean(),
   isExemptFromAB: z.boolean(),
   partner: z.enum(['Back Market', 'THD']).nullable(),
   subscriptionContracts: z.array(
      z.object({
         productHandle: z.string(),
         storeCode: z.string(),
         sku: z.string().nullable().optional(),
      })
   ),
});

const UserApiResponseSchema = z.object({
   userid: z.number(),
   username: z.string(),
   unique_username: z.string().optional().nullable(),
   login: z.string(),
   image: z
      .object({
         thumbnail: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
   algoliaApiKeyProducts: z.string().optional().nullable(),
   priceTiers: z.record(z.string()),
   privileges: z.array(z.string()),
   teams: z.array(z.number()),
   links: z.record(z.any()),
   is_employee: z.boolean().optional(),
   unread_notification_count: z.number().optional().nullable(),
   isExemptFromAB: z.boolean(),
   partner: z.enum(['Back Market', 'THD']).nullable().optional(),
   subscriptionContracts: z
      .array(
         z.object({
            productHandle: z.string(),
            storeCode: z.string(),
            sku: z.string().nullable().optional(),
         })
      )
      .optional()
      .default([]),
});

export async function fetchAuthenticatedUser(
   apiClient: IFixitAPIClient,
   init?: RequestInit
): Promise<User | null> {
   const response = await apiClient.getRaw('user', 'user', init);
   if (response.status === 401 || response.status === 403) {
      return null;
   } else if (!response.ok) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
      apiClient.throwFetchError(response, {
         input: 'user',
         init,
      });
   }
   const payload = await response.json();
   if (payload == null) {
      return null;
   }
   const userSchema = UserApiResponseSchema.safeParse(payload);
   if (!userSchema.success) {
      captureMessage('User schema parsing failed', {
         level: 'debug',
         extra: {
            schema: userSchema,
            payload: payload,
            error: userSchema.error,
         },
      });
      return null;
   }
   const isAdmin = userSchema.data.privileges.includes('Admin');
   const isEmployee = userSchema.data?.is_employee ?? false;
   return {
      id: userSchema.data.userid,
      username: userSchema.data.username,
      handle: userSchema.data.unique_username ?? null,
      login: userSchema.data.login,
      thumbnail: userSchema.data.image?.thumbnail ?? null,
      algoliaApiKeyProducts: userSchema.data.algoliaApiKeyProducts ?? null,
      priceTiers: userSchema.data.priceTiers,
      isAdmin: isAdmin,
      teams: userSchema.data.teams,
      links: userSchema.data.links,
      unread_notification_count: userSchema.data.unread_notification_count,
      privileges: userSchema.data.privileges,
      isEmployee: isEmployee,
      isExemptFromAB: userSchema.data?.isExemptFromAB ?? false,
      partner: userSchema.data.partner ?? null,
      subscriptionContracts: userSchema.data.subscriptionContracts ?? [],
   };
}
